import {
  Component,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
  ElementRef,
  OnDestroy,
  forwardRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
  SimpleChanges,
  OnChanges,
  SimpleChange,
} from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { NO_DROPDOWN_DATA } from 'src/defaultImages.js';

// Provider for ControlValueAccessor, allowing the component to act as a form control
export const DROPDOWN_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiselectDropdownComponent),
  multi: true,
};

@Component({
  selector: 'multi-select',
  templateUrl: './multiselect-dropdown.component.html',
  styleUrls: ['./multiselect-dropdown.component.css'],
  providers: [DROPDOWN_CONTROL_VALUE_ACCESSOR], // Registering the component as a control value accessor
  changeDetection: ChangeDetectionStrategy.OnPush, // Optimizes performance by reducing change detection frequency
})
export class MultiselectDropdownComponent
  implements OnChanges, OnDestroy, ControlValueAccessor
{
  // Input properties to customize the component's behavior and appearance
  @Input() dropdownName: string = 'Dropdown';
  @Input() options: any[] = []; // Options array to be displayed in the dropdown
  @Input() valueKey: string = 'id'; // Key to identify the value of each option
  @Input() labelKey: string = 'label'; // Key to identify the label of each option
  @Input() disabledKey: string = 'disabled'; // Key to check if an option is disabled
  @Input() searchEnable: boolean = false; // Toggle for enabling search functionality
  @Input() selectAllEnable: boolean = true; // Toggle for enabling "Select All" functionality
  @Input() style: {
    width?: string;
    height?: string;
  } = {
    width: '100%',
    height: '44px',
  };
  @Input() isFilterMode: boolean = true;
  @Input() placeholder: string = '';

  images = {
    NO_DROPDOWN_DATA,
  };
  allOptions: any[] = []; // Full list of options (used as the source of truth)
  filteredOptions: any[] = []; // Options that match the current search criteria
  selectedItemsCount: number = 0; // Counter to keep track of selected options
  overlayRef: OverlayRef | null = null; // Reference to the overlay used for the dropdown
  isDisabled: boolean = false; // Indicates if the dropdown is disabled
  isOpen: boolean = false; // Tracks whether the dropdown is open or closed
  searchText: string = ''; // Current search text for filtering options
  allSelected: boolean = false; // Indicates whether all options are selected

  private filterOptionsDebounce: Subject<string> = new Subject<string>();
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  // Store pending value to apply when options become available
  // Use Symbol as sentinel value to distinguish "no pending value" from null/undefined values
  private readonly NO_PENDING_VALUE = Symbol('no-pending-value');
  private pendingValue: any = this.NO_PENDING_VALUE;

  @ViewChild('dropdownTemplate') dropdownTemplate: TemplateRef<any>; // Template reference for the dropdown content
  @ViewChild('dropdownOrigin', { read: ElementRef }) dropdownOrigin: ElementRef; // Element reference for the dropdown trigger

  private onChange: (value: any) => void = () => {}; // Function to call when the value changes
  private onTouched: () => void = () => {}; // Function to call when the component is touched

  constructor(
    private overlay: Overlay, // Service to create overlay elements
    private viewContainerRef: ViewContainerRef, // Reference to the view container
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.optionsChanged(changes['options'])) {
      const previousSelectedOptions = this.getSelectedOptions();
      this.initializeOptions();

      if (
        this.pendingValue !== this.NO_PENDING_VALUE &&
        this.allOptions.length > 0
      ) {
        this.applyPendingValue();
      } else {
        this.retainSelectedOptions(previousSelectedOptions);
      }

      this.updateSelectedItemsCount();
    }
  }

  ngOnInit(): void {
    this.initializeOptions();
    this.setupDebouncedFilter();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next(); // Emit a value to signal the teardown
    this.ngUnsubscribe.complete(); // Complete the ngUnsubscribe subject
    this.closeDropdown(); // Ensure the dropdown is closed and cleaned up
  }

  private setupDebouncedFilter(): void {
    this.filterOptionsDebounce
      .pipe(
        debounceTime(300), // Wait for 300ms pause in events
        distinctUntilChanged(),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((searchText) => {
        this.filteredOptions = this.allOptions?.filter((option) =>
          option[this.labelKey]?.toLowerCase().includes(searchText)
        );
      });
  }

  // Initialize and map options to internal format
  private initializeOptions(): void {
    this.allOptions = this.options.map((option) => {
      if (typeof option === 'object') {
        // Handle object options with specified keys
        return {
          [this.valueKey]: option[this.valueKey],
          [this.labelKey]: option[this.labelKey],
          isSelected: option?.isSelected || false,
          isDisabled: option[this.disabledKey] || false,
        };
      } else if (
        typeof option === 'string' ||
        typeof option === 'number' ||
        Array.isArray(option)
      ) {
        // Handle primitive options (strings or numbers)
        return {
          [this.valueKey]: option,
          [this.labelKey]: option,
          isSelected: false,
          isDisabled: false,
        };
      }
      return option;
    });
    // Initialize filtered options
    this.filteredOptions = this.allOptions.slice();
  }

  // Toggles the dropdown's open/close state
  toggleDropdown(): void {
    if (this.isDisabled) return; // Do nothing if the dropdown is disabled
    this.isOpen = !this.isOpen;
    this.changeDetector.detectChanges(); // Ensure the view updates immediately
    if (this.isOpen) {
      this.openDropdown(); // Open the dropdown if it's currently closed
    } else {
      this.closeDropdown(); // Close the dropdown if it's currently open
    }
  }

  // Opens the dropdown by creating an overlay
  openDropdown(): void {
    if (this.overlayRef) {
      return; // If the overlay is already open, do nothing
    }

    const width = this.dropdownOrigin?.nativeElement?.offsetWidth;

    const overlayConfig = this.getOverlayConfig();
    overlayConfig.minWidth = width;

    // Create the overlay with specified configuration
    this.overlayRef = this.overlay.create(overlayConfig);

    const templatePortal = new TemplatePortal(
      this.dropdownTemplate,
      this.viewContainerRef
    );
    // Attach the dropdown template to the overlay
    this.overlayRef.attach(templatePortal);

    // Close the dropdown when the backdrop is clicked
    this.overlayRef.backdropClick().subscribe(() => this.closeDropdown());
  }

  // Closes the dropdown by disposing of the overlay
  closeDropdown(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose(); // Dispose of the overlay
      this.overlayRef = null; // Clear the overlay reference
      this.isOpen = false;
      this.changeDetector.markForCheck();
    }
  }

  // Handles option selection logic
  onOptionClick(option: any): void {
    if (this.isDisabled) return; // Do nothing if the dropdown is disabled
    this.selectedItemsCount = option?.isSelected
      ? this.selectedItemsCount + 1
      : this.selectedItemsCount - 1; // Update the selected items count
    this.checkAllSelected();
    this.onChange(this.getSelectedOptions()); // Notify value change
    this.onTouched(); // Mark component as touched
    this.changeDetector.markForCheck();
  }

  // Updates the filtered options based on the search text
  onFilterTextChange(searchText: string): void {
    this.searchText = searchText?.toLowerCase();
    this.filterOptionsDebounce.next(this.searchText);
  }

  // Clears all selected options
  clearSelectedOptions(): void {
    if (this.isDisabled) return; // Do nothing if the dropdown is disabled
    this.selectedItemsCount = 0;
    this.searchText = '';
    this.filteredOptions = this.allOptions?.map((option) => ({
      ...option,
      isSelected: false,
    }));
    this.allOptions = this.filteredOptions.slice();
    this.onChange([]); // Notify that all options are deselected
    this.onTouched(); // Mark component as touched
    this.changeDetector.markForCheck();
  }

  // Clears the search filter
  emptySearchFilter(): void {
    this.onFilterTextChange(''); // Reset the search text
  }

  // Retrieves the configuration for the dropdown overlay
  protected getOverlayConfig(): OverlayConfig {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.dropdownOrigin) // Position the overlay relative to the dropdown origin
      .withPush(false)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ]);

    const scrollStrategy = this.overlay.scrollStrategies.reposition(); // Handle scrolling by repositioning the overlay

    return new OverlayConfig({
      positionStrategy: positionStrategy,
      scrollStrategy: scrollStrategy,
      hasBackdrop: true, // Add a backdrop when the overlay is open
      backdropClass: 'cdk-overlay-transparent-backdrop', // Use a transparent backdrop
    });
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    // Store the value for later use if options are not available yet
    this.pendingValue = value;

    // Guard clause – exit early if value or allOptions are not present
    if (!Array.isArray(this.allOptions) || !this.allOptions.length) {
      return;
    }

    // Clear pending state since we're applying the value now
    this.pendingValue = this.NO_PENDING_VALUE;

    // Reset the selected count
    this.selectedItemsCount = 0;

    // Handle null or undefined
    if (value === null || value === undefined) {
      this.allOptions = this.allOptions.map((option) => ({
        ...option,
        isSelected: false,
      }));
      this.filteredOptions = [...this.allOptions];
      this.checkAllSelected();
      this.changeDetector.markForCheck();
      return;
    }

    // Convert to array if not already
    const valueArray = Array.isArray(value) ? value : [value];

    // If empty array, clear all selections
    if (valueArray.length === 0) {
      this.allOptions = this.allOptions.map((option) => ({
        ...option,
        isSelected: false,
      }));
      this.filteredOptions = [...this.allOptions];
      this.checkAllSelected();
      this.changeDetector.markForCheck();
      return;
    }

    // Determine if we're dealing with an array of primitives (IDs) or objects
    const isPrimitiveArray = valueArray.some(
      (item) =>
        typeof item === 'string' ||
        typeof item === 'number' ||
        Array.isArray(item)
    );

    // Update options based on input format
    if (isPrimitiveArray) {
      // Handle array of IDs format
      this.allOptions = this.allOptions.map((option) => {
        const isSelected = valueArray.includes(option[this.valueKey]);
        if (isSelected) this.selectedItemsCount++;

        return {
          ...option,
          isSelected,
        };
      });
    } else {
      // Handle array of objects format (traditional approach)
      // Build a Map for efficient lookup: id -> isSelected
      const selectionMap = new Map<string, boolean>();
      for (const item of valueArray) {
        const key = item?.[this.valueKey];
        if (key !== undefined) {
          selectionMap.set(key, !!item.isSelected);
          if (item.isSelected) this.selectedItemsCount++;
        }
      }

      // Update allOptions with correct isSelected status
      this.allOptions = this.allOptions.map((option) => {
        const key = option?.[this.valueKey];
        const isSelected = selectionMap.get(key) ?? false;
        return {
          ...option,
          isSelected,
        };
      });
    }

    // Sync filtered options (if used in filtering/search)
    this.filteredOptions = [...this.allOptions];

    // Update any check-all flags or UI triggers
    this.checkAllSelected();

    this.changeDetector.markForCheck();
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn; // Set the function to call when the value changes
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn; // Set the function to call when the component is touched
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled; // Set the disabled state of the component
  }

  // Helper method to get the selected options based on the valueKey
  private getSelectedOptions(): any[] {
    return this.allOptions
      .filter((opt) => opt.isSelected) // Filter to include only selected options
      .map((opt) => opt[this.valueKey]); // Return the values of the selected options
  }

  toggleSelectAll() {
    // Update the selection status of each option
    this.filteredOptions.forEach((option) => {
      if (!option.isDisabled) {
        option.isSelected = this.allSelected;
      }
    });

    // Ensure the selected item count is updated
    this.updateSelectedItemsCount();
    this.onChange(this.getSelectedOptions());
    this.onTouched();
    this.changeDetector.markForCheck();
  }

  // Method to update selected items count and adjust allSelected state
  updateSelectedItemsCount() {
    this.selectedItemsCount = this.filteredOptions.filter(
      (option) => option.isSelected
    ).length;
    this.checkAllSelected();
  }

  // Ensure allSelected is true only if all items are selected
  checkAllSelected() {
    this.allSelected = this.allOptions.every((option) => option.isSelected);
  }

  get viewportHeight(): number {
    const numberOfOptions = this.filteredOptions.length;
    const totalHeight = numberOfOptions * 48 + 4;
    // Return the lesser of the total required height or the maximum allowed height
    return Math.min(totalHeight, 250);
  }

  // Checks if the options input has changed.
  private optionsChanged(optionChange: SimpleChange): boolean {
    const previousValue = optionChange?.previousValue;
    const currentValue = optionChange?.currentValue;
    return !this.areOptionsEqual(previousValue, currentValue);
  }

  // Retains previously selected options that still exist in the new options list.
  private retainSelectedOptions(previousSelectedOptions: any[]): void {
    this.allOptions = this.allOptions?.map((option) => ({
      ...option,
      isSelected: previousSelectedOptions?.includes(option[this.valueKey]),
    }));
    this.filteredOptions = [...this.allOptions];
  }

  // Checks if the previous and current options arrays are equivalent.
  private areOptionsEqual(
    previousOptions: any[],
    currentOptions: any[]
  ): boolean {
    return JSON.stringify(previousOptions) === JSON.stringify(currentOptions);
  }

// Apply pending value when async options load to fix dropdown value selection.
  private applyPendingValue(): void {
    if (this.pendingValue !== this.NO_PENDING_VALUE) {
      const valueToApply = this.pendingValue;
      this.pendingValue = this.NO_PENDING_VALUE;
      this.writeValue(valueToApply);
    }
  }
}
